import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";
import { ClientSettings } from "src/model/class/client-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { BIGService } from "src/service/big.service";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { PopupService } from "src/service/popup.service";
import { SystemRouterService } from "src/service/system-router.service";
import { ThemeService } from "src/service/theme.service";
import { TranslationService } from "src/service/translation.service";
import {
  clientSettingsSetSupervisionModeViewAction,
  clientSettingsUpdateKioskModeAction,
  clientSettingsUpdateKioskModePasswordAction,
  clientSettingsUpdateShowButtonConnectionExitAction,
  clientSettingsUpdateTimeInactivitySupervision,
} from "src/state/actions/client-settings.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { clientSettings } from "src/state/selectors/client-settings.selectors";

@Component({
    selector: "app-options-settings-generic",
    templateUrl: "./options-settings-generic.component.html",
    styleUrls: ["./options-settings-generic.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OptionsSettingsGenericComponent implements OnInit, iEvent {
  //#region Subcriptions to "_store.select(...)" Observables
  private _selectClientSettingsSub: Subscription = null;
  private _selectcurrentUserSub: Subscription = null;

  //#endregion

  private _logContext: string = "Options-settings-generic";
  private _formSubscriptions: Array<Subscription> = new Array<Subscription>();
  private _networkServiceSubscription: Subscription = null;
  private _currentClientSetting: ClientSettings = null;
  private _currentUser: CurrentSessionData = null;
  private _currentLanguage: string = "it";

  public _eventSubscriptions: Array<Subscription> = new Array<Subscription>();
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public isAndroid: boolean = false;
  public isCordovaReady: boolean = false;
  public isSIBODevice: boolean = false;
  public isBigStudio: boolean = false;
  /** Languages offered in the settings (names in their own language, as is customary) */
  public readonly languages: Array<{ code: string; name: string; flag: string }> = [
    { code: "it", name: "Italiano", flag: "assets/img/flag/italy.svg" },
    { code: "en", name: "English", flag: "assets/img/flag/united-kingdom.svg" },
    { code: "de", name: "Deutsch", flag: "assets/img/flag/germany.svg" },
    { code: "es", name: "Español", flag: "assets/img/flag/spain.svg" },
    { code: "fr", name: "Français", flag: "assets/img/flag/france.svg" },
  ];

  /**
   * Select a language by tapping anywhere on its tile (saved through the form valueChanges)
   */
  public onClickLanguage(code: string): void {
    const control = this.formAppSettings.get("languageInUse");
    if (control.value !== code) {
      control.setValue(code);
    }
  }

  public formAppSettings: UntypedFormGroup = null;
  public langRadioGroupValue: string = "it";
  public toolBarColor: string = "primary";

  public passwordIconType: string = "eye";
  public passwordInputType: string = "password";
  public cssPlatformClass: string = "";

  constructor(
    private _popupService: PopupService,
    private _eventService: EventService,
    private _networkService: NetworkService,
    public _translationService: TranslationService,
    private _fb: UntypedFormBuilder,
    private _loggerService: LoggerService,
    private _store: Store,
    private _platform: Platform,
    private _bigService: BIGService,
    private _themeService: ThemeService,
    private translate: TranslateService,
    private _systemRouterService: SystemRouterService
  ) {
    if (this._platform.is("ios")) {
      this.cssPlatformClass = "ios";
    }
  }

  ngOnInit() {
    /** Resetting password type */
    this.passwordInputType = "password";
    this.passwordIconType = "eye";

    this.isSIBODevice = this._bigService.isMyToDevice;

    this.subscribeEvents();
    this.subscribeNetworkServiceData();

    this.initializeForm();
    this.subscribeSettingServiceData();
    this.loadCurrentUser();

    this.subscribeFormEvent();
  }

  ngOnDestroy() {
    this.unsubscribeEvents();
    this.unsubscribeFormEvent();

    if (this._networkServiceSubscription) {
      this._networkServiceSubscription.unsubscribe();
    }

    this.unsubscribeStoreSelect();
  }

  /**
   * Handle click open popup request change password
   */
  public async onClickChangePassword(): Promise<void> {
    const newPassword: string =
      await this._popupService.showModalRequestPassword(
        this._currentClientSetting.kioskModePassword
      );

    if (newPassword) {
      this.formAppSettings.patchValue({
        kioskModePassword: newPassword,
      });
    }
  }

  /**
   * Avoid prompt number in custom input text // ///
   *
   * @param event
   * @returns
   */
  public promptOnlyNumber(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;

    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  /**
   * Change input type for password input
   */
  public onClickChangeInputTypePasswordUser() {
    if (this.passwordInputType === "password") {
      this.passwordInputType = "input";
      this.passwordIconType = "eye-off";
    } else {
      this.passwordInputType = "password";
      this.passwordIconType = "eye";
    }
  }

  //#region Event service
  /**
   * Subscribe event from event service
   */
  public subscribeEvents(): void {
    this._eventSubscriptions.push(
      this._eventService.OnCordovaIsReadyToRumble.subscribe(
        (isCordovaReady: boolean) => {
          this.isCordovaReady = isCordovaReady;
        }
      )
    );

    this._eventSubscriptions.push(
      this._eventService.OnIsAndroidDevice.subscribe(
        (isAdroidDevice: boolean) => {
          this.isAndroid = isAdroidDevice;
        }
      )
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          if (this._currentUser) {
            this.currentConnectionType = currentConnectionType;

            this.toolBarColor = StaticClass.getComponentColorByNetworkState(
              this.currentConnectionType
            );
          } else {
            this.toolBarColor = "primary";
          }
        }
      );
  }

  /**
   * Initialize form settings field with default value
   */
  private initializeForm(): void {
    this.langRadioGroupValue = "it";

    this.formAppSettings = this._fb.group({
      languageInUse: new UntypedFormControl("it"),
      kioskMode: new UntypedFormControl(false),

      //autoStartOnBoot: new FormControl(false),

      kioskModePassword: new UntypedFormControl(""),
      lostConnectionExitBtn: new UntypedFormControl(true),
      showSupervisionListMode: new UntypedFormControl(true),
      timeInactivity: new UntypedFormControl(""),
    });
  }

  /**
   * Update form field with settings storage values
   */
  private updateFormField(): void {
    this.langRadioGroupValue = this._currentClientSetting.language;

    this.formAppSettings.patchValue(
      {
        languageInUse: this._currentClientSetting.language,

        kioskMode:
          //this._currentClientSetting.kioskMode.toString().toUpperCase() === "TRUE",
          StaticClass.booleanize(this._currentClientSetting.kioskMode),

        /*
                autoStartOnBoot:
                  StaticClass.booleanize(
                    this._currentClientSetting.isAutoStartOnBootUpON
                  ),
                  */

        kioskModePassword: this._currentClientSetting.kioskModePassword,

        lostConnectionExitBtn: StaticClass.booleanize(
          this._currentClientSetting.isConnectionLostExitButtonEnabled
        ),

        showSupervisionListMode: StaticClass.booleanize(
          this._currentClientSetting.supervisionListViewMode
        ),
        timeInactivity: this._currentClientSetting.timeInactivitySupervision,
      },
      { emitEvent: false }
    );
  }

  /**
   * Subscribe value change event from each form field.
   * There are different subscription because each form field handle specific
   * event service propagation //
   */
  private subscribeFormEvent() {
    this._formSubscriptions = new Array<Subscription>();

    this._formSubscriptions.push(
      this.formAppSettings
        .get("kioskMode")
        .valueChanges.subscribe(async (kioskMode: boolean) => {
          this._store.dispatch(
            clientSettingsUpdateKioskModeAction({
              kioskMode,
            })
          );
        })
    );

    /*
    this._formSubscriptions.push(
      this.formAppSettings
        .get("autoStartOnBoot")
        .valueChanges.subscribe((autoStartOnReboot: boolean) => {
          if (this.isAndroid && this.isCordovaReady && !this.isSIBODevice) {
            this.onAutoStartChanged(autoStartOnReboot);
          }

          this._store.dispatch(
            clientSettingsUpdateAutoRestartOnBootAction({
              autoStartOnReboot,
            })
          );
        })
    );
    */

    this._formSubscriptions.push(
      this.formAppSettings
        .get("kioskModePassword")
        .valueChanges.subscribe((formUpdatedField: string) => {
          this._store.dispatch(
            clientSettingsUpdateKioskModePasswordAction({
              kioskModePassword: formUpdatedField,
            })
          );
        })
    );

    this._formSubscriptions.push(
      this.formAppSettings
        .get("lostConnectionExitBtn")
        .valueChanges.subscribe((formUpdatedField: boolean) => {
          this._store.dispatch(
            clientSettingsUpdateShowButtonConnectionExitAction({
              showButtonCommandExit: formUpdatedField,
            })
          );
        })
    );

    this._formSubscriptions.push(
      this.formAppSettings
        .get("languageInUse")
        .valueChanges.subscribe((formUpdatedField: string) => {
          this.changeCurrentLanguage(formUpdatedField);
        })
    );

    this._formSubscriptions.push(
      this.formAppSettings
        .get("showSupervisionListMode")
        .valueChanges.subscribe((showSupervisionListMode: boolean) => {
          this._store.dispatch(
            clientSettingsSetSupervisionModeViewAction({
              viewMode: showSupervisionListMode,
            })
          );
        })
    );

    this._formSubscriptions.push(
      this.formAppSettings
        .get("timeInactivity")
        .valueChanges.subscribe(async (timeInactivity: number) => {
          this.updateTimeInactivity();
        })
    );
  }

  /**
   * Save timeInactivity into the store
   */
  public updateTimeInactivity(): void {
    this._store.dispatch(
      clientSettingsUpdateTimeInactivitySupervision({
        timeInactivitySupervision:
          this.formAppSettings.get("timeInactivity").value,
      })
    );
  }

  private unsubscribeFormEvent() {
    for (const sub of this._formSubscriptions) {
      sub.unsubscribe();
    }
  }

  private subscribeSettingServiceData(): void {
    this._selectClientSettingsSub = this._store
      .select(clientSettings)
      .pipe(
        filter((clientSetting) => clientSetting !== null),
        tap((clientSetting) => (this._currentClientSetting = clientSetting))
      )
      .subscribe((clientSettings: ClientSettings) => {
        this.updateFormField();
      });
  }

  //NOTE: CURRENTLY NOT USED
  /**
   * Enable/disable the app's ability to self-launch on device's boot-up.
   *
   * WARNING: The Cordova-Android plugin on which this function is based is functional ONLY in an Android
   * environment. This method should be called only in such environment.
   *
   * ALSO, this method should be called ONLY AFTER we are sure that the Cordova-engine is fully ready.
   *
   * @param isAutoStartEnable Handle autostart on boot
   */
  /*private onAutoStartChanged(isAutoStartEnable: boolean) {
    if (isAutoStartEnable) {
      this._autostart.enable();
    } else {
      this._autostart.disable();
    }
  }*/

  /**
   * Change current system language and save it to settings
   *
   * @param newLanguage New language to use
   */
  private changeCurrentLanguage(newLanguage: string): void {
    this._currentLanguage = newLanguage;

    this._translationService.changLang(this._currentLanguage);
  }

  /**
   * Load current user from store
   */
  private loadCurrentUser(): void {
    this._selectcurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser)
      )
      .subscribe((currentUser: CurrentSessionData) => {
        this._currentUser = currentUser;
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectClientSettingsSub) {
      this._selectClientSettingsSub.unsubscribe();
    }

    if (this._selectcurrentUserSub) {
      this._selectcurrentUserSub.unsubscribe();
    }
  }

  //#endregion
}
